// Extend Window interface to include toast
declare global {
    interface Window {
        toast?: {
            success: (message: string) => void;
            error: (message: string) => void;
        };
    }
}

class BrowserNotificationService {
    private static instance: BrowserNotificationService;
    private permission: NotificationPermission = 'default';
    private _isSupported: boolean = false;
    private notificationQueue: Array<{ title: string; options?: NotificationOptions }> = [];
    private isProcessingQueue: boolean = false;

    private constructor() {
        this._isSupported = 'Notification' in window;
        if (this._isSupported) {
            this.permission = Notification.permission;
        }
    }

    public static getInstance(): BrowserNotificationService {
        if (!BrowserNotificationService.instance) {
            BrowserNotificationService.instance = new BrowserNotificationService();
        }
        return BrowserNotificationService.instance;
    }

    public async requestPermission(): Promise<boolean> {
        if (!this._isSupported) {
            console.warn('Browser notifications are not supported');
            return false;
        }

        if (this.permission === 'granted') {
            return true;
        }

        if (this.permission === 'denied') {
            console.warn('Notification permission denied');
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            return permission === 'granted';
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    public async showNotification(title: string, options: NotificationOptions = {}): Promise<boolean> {
        if (!this._isSupported) {
            return false;
        }

        if (this.permission !== 'granted') {
            const granted = await this.requestPermission();
            if (!granted) {
                return false;
            }
        }

        try {
            // Enhanced notification options
            const enhancedOptions: NotificationOptions = {
                icon: '/favicon.png',
                badge: '/favicon.png',
                requireInteraction: false,
                silent: false,
                vibrate: [200, 100, 200], // Vibration pattern
                ...options
            };

            // Actions are only supported with ServiceWorkerRegistration.showNotification(), not new Notification()
            delete (enhancedOptions as Record<string, unknown>).actions;

            // Enhanced options based on notification type
            if (options.data?.notification_type === 'login_alert') {
                this.enhanceLoginNotificationOptions(enhancedOptions, options.data);
            }

            // Add sound for urgent notifications
            if (options.data?.type === 'urgent' || options.data?.type === 'urgent_reminder' || 
                options.data?.risk_level === 'high' || options.data?.risk_level === 'critical') {
                enhancedOptions.silent = false;
                enhancedOptions.requireInteraction = true;
                enhancedOptions.vibrate = [300, 100, 300, 100, 300]; // More intense vibration
            }

            const notification = new Notification(title, enhancedOptions);

            // Auto close after appropriate time
            const autoCloseTime = enhancedOptions.requireInteraction ? 10000 : 5000;
            setTimeout(() => {
                if (notification) {
                    notification.close();
                }
            }, autoCloseTime);

            // Enhanced click event handling
            notification.onclick = (event) => {
                event.preventDefault();
                notification.close();
                this.handleNotificationClick(options.data);
            };

            notification.onclose = () => {
                this.handleNotificationClose(options.data);
            };

            notification.onerror = (error) => {
                console.error('Notification error:', error);
                this.handleNotificationError(options.data, error);
            };

            // Handle notification close
            notification.onclose = () => {
                console.log('Notification closed:', title);
            };

            // Handle notification error
            notification.onerror = (error) => {
                console.error('Notification error:', error);
            };

            return true;
        } catch (error) {
            console.error('Error showing notification:', error);
            return false;
        }
    }

    public async showMultipleNotifications(notifications: Array<{ title: string; options?: NotificationOptions }>): Promise<number> {
        let successCount = 0;
        
        // Process notifications with a small delay to avoid overwhelming the user
        for (const notification of notifications) {
            const success = await this.showNotification(notification.title, notification.options);
            if (success) {
                successCount++;
                // Add a small delay between notifications
                await new Promise(resolve => setTimeout(resolve, 200));
            }
        }
        
        return successCount;
    }

    public async showQueuedNotifications(): Promise<number> {
        if (this.isProcessingQueue || this.notificationQueue.length === 0) {
            return 0;
        }

        this.isProcessingQueue = true;
        let successCount = 0;

        try {
            while (this.notificationQueue.length > 0) {
                const notification = this.notificationQueue.shift();
                if (notification) {
                    const success = await this.showNotification(notification.title, notification.options);
                    if (success) {
                        successCount++;
                    }
                    // Small delay between notifications
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
        } finally {
            this.isProcessingQueue = false;
        }

        return successCount;
    }

    public queueNotification(title: string, options?: NotificationOptions): void {
        this.notificationQueue.push({ title, options });
        
        // Process queue if not already processing
        if (!this.isProcessingQueue) {
            setTimeout(() => this.showQueuedNotifications(), 100);
        }
    }

    private enhanceLoginNotificationOptions(enhancedOptions: NotificationOptions, data: any): void {
        // Set risk-based options
        const riskLevel = data.risk_level;
        
        switch (riskLevel) {
            case 'low':
                enhancedOptions.silent = true;
                enhancedOptions.requireInteraction = false;
                enhancedOptions.vibrate = [100, 50, 100];
                break;
            case 'medium':
                enhancedOptions.silent = false;
                enhancedOptions.requireInteraction = false;
                enhancedOptions.vibrate = [200, 100, 200];
                break;
            case 'high':
                enhancedOptions.silent = false;
                enhancedOptions.requireInteraction = true;
                enhancedOptions.vibrate = [300, 100, 300, 100, 300];
                break;
            case 'critical':
                enhancedOptions.silent = false;
                enhancedOptions.requireInteraction = true;
                enhancedOptions.vibrate = [500, 200, 500, 200, 500, 200, 500];
                break;
        }

        // Add action buttons if available
        if (data.actions && Array.isArray(data.actions)) {
            this.addActionButtons(enhancedOptions, data.actions);
        }
    }

    private addActionButtons(enhancedOptions: NotificationOptions, actions: any[]): void {
        // For browsers that support action buttons (mainly Chrome)
        if ('actions' in Notification.prototype) {
            enhancedOptions.actions = actions.map(action => ({
                action: action.action,
                title: action.title,
                icon: action.icon || '/favicon.png'
            }));
        }

        // Store actions in data for custom handling
        enhancedOptions.data = {
            ...enhancedOptions.data,
            availableActions: actions
        };
    }

    private async markNotificationAsRead(notificationId: string): Promise<void> {
        try {
            const authUser = localStorage.getItem('authUser');
            let token = '';
            if (authUser) {
                try {
                    token = JSON.parse(authUser);
                } catch (error) {
                    console.warn('Failed to parse auth token:', error);
                    return;
                }
            }

            const response = await fetch(`/api/notifications/${notificationId}/mark-read`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                console.log('Notification marked as read:', notificationId);
            } else {
                console.warn('Failed to mark notification as read:', notificationId);
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }

    private handleNotificationClick(data: any): void {
        if (!data) return;

        // Handle different notification types
        switch (data.notification_type) {
            case 'login_alert':
                this.handleLoginNotificationClick(data);
                break;
            case 'security_alert':
                this.handleSecurityNotificationClick(data);
                break;
            default:
                this.handleDefaultNotificationClick(data);
        }
    }

    private handleLoginNotificationClick(data: any): void {
        // Focus the window
        if (window.focus) {
            window.focus();
        }

        // Navigate to login history page
        if (data.action_url) {
            if (window.location.pathname !== data.action_url) {
                window.location.href = data.action_url;
            }
        }

        // Mark notification as read
        if (data.notification_id) {
            this.markNotificationAsRead(data.notification_id);
        }

        // Show action buttons if available
        if (data.actions && data.actions.length > 0) {
            this.showActionButtons(data);
        }
    }

    private handleSecurityNotificationClick(data: any): void {
        // Focus the window
        if (window.focus) {
            window.focus();
        }

        // Navigate to security settings
        if (data.action === 'account_secured') {
            window.location.href = '/security/settings';
        }
    }

    private handleDefaultNotificationClick(data: any): void {
        // Focus the window
        if (window.focus) {
            window.focus();
        }

        // Navigate to action URL if available
        if (data.action_url) {
            if (window.location.pathname !== data.action_url) {
                window.location.href = data.action_url;
            }
        }

        // Mark notification as read
        if (data.notification_id) {
            this.markNotificationAsRead(data.notification_id);
        }
    }

    private showActionButtons(data: any): void {
        // Create a custom action button modal
        const modal = document.createElement('div');
        modal.className = 'notification-action-modal';
        modal.innerHTML = `
            <div class="notification-action-content">
                <h3>Login Alert Actions</h3>
                <div class="action-buttons">
                    ${JSON.parse(data.actions || '[]').map((action: any) => `
                        <button class="action-btn action-${action.action}" data-action="${action.action}" data-url="${action.url}">
                            ${action.title}
                        </button>
                    `).join('')}
                </div>
                <button class="close-btn">Close</button>
            </div>
        `;

        // Add styles
        const style = document.createElement('style');
        style.textContent = `
            .notification-action-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10000;
            }
            .notification-action-content {
                background: white;
                padding: 20px;
                border-radius: 8px;
                max-width: 400px;
                width: 90%;
            }
            .action-buttons {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin: 15px 0;
            }
            .action-btn {
                padding: 10px 15px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                font-size: 14px;
            }
            .action-trust_device { background: #4CAF50; color: white; }
            .action-secure_account { background: #f44336; color: white; }
            .action-report_suspicious { background: #ff9800; color: white; }
            .action-view_details { background: #2196F3; color: white; }
            .close-btn {
                padding: 8px 16px;
                border: 1px solid #ddd;
                border-radius: 4px;
                background: #f5f5f5;
                cursor: pointer;
            }
        `;

        document.head.appendChild(style);
        document.body.appendChild(modal);

        // Add event listeners
        modal.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            
            if (target.classList.contains('close-btn') || target === modal) {
                document.body.removeChild(modal);
                return;
            }

            if (target.classList.contains('action-btn')) {
                const action = target.getAttribute('data-action');
                const url = target.getAttribute('data-url');
                
                this.handleActionButtonClick(action, url, data);
                document.body.removeChild(modal);
            }
        });
    }

    private async handleActionButtonClick(action: string | null, url: string | null, data: any): Promise<void> {
        if (!action) return;

        try {
            switch (action) {
                case 'trust_device':
                    await this.trustDevice(data.device_id);
                    break;
                case 'secure_account':
                    await this.secureAccount(data.device_id);
                    break;
                case 'report_suspicious':
                    await this.reportSuspiciousLogin(data.device_id);
                    break;
                case 'view_details':
                    if (url) {
                        window.location.href = url;
                    }
                    break;
            }
        } catch (error) {
            console.error('Error handling action button:', error);
        }
    }

    private async trustDevice(deviceId: string): Promise<void> {
        try {
            const response = await fetch('/api/users/trust-device', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({ device_id: deviceId })
            });

            if (response.ok) {
                console.log('Device marked as trusted');
                this.showToast('Device marked as trusted', 'success');
            } else {
                throw new Error('Failed to trust device');
            }
        } catch (error) {
            console.error('Error trusting device:', error);
            this.showToast('Failed to trust device', 'error');
        }
    }

    private async secureAccount(deviceId: string): Promise<void> {
        try {
            const response = await fetch('/api/users/secure-account', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({ current_device_id: deviceId })
            });

            if (response.ok) {
                console.log('Account secured');
                this.showToast('Account secured successfully', 'success');
            } else {
                throw new Error('Failed to secure account');
            }
        } catch (error) {
            console.error('Error securing account:', error);
            this.showToast('Failed to secure account', 'error');
        }
    }

    private async reportSuspiciousLogin(deviceId: string): Promise<void> {
        try {
            const response = await fetch('/api/users/report-suspicious-login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({ 
                    device_id: deviceId,
                    reason: 'User reported suspicious login via notification'
                })
            });

            if (response.ok) {
                console.log('Suspicious login reported');
                this.showToast('Suspicious login reported', 'success');
            } else {
                throw new Error('Failed to report suspicious login');
            }
        } catch (error) {
            console.error('Error reporting suspicious login:', error);
            this.showToast('Failed to report suspicious login', 'error');
        }
    }

    private showToast(message: string, type: 'success' | 'error'): void {
        // Use toastify or create a simple toast
        if (window.toast) {
            window.toast[type](message);
        } else {
            // Simple toast implementation
            const toast = document.createElement('div');
            toast.className = `toast toast-${type}`;
            toast.textContent = message;
            toast.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                padding: 10px 20px;
                border-radius: 4px;
                color: white;
                z-index: 10001;
                ${type === 'success' ? 'background: #4CAF50;' : 'background: #f44336;'}
            `;
            
            document.body.appendChild(toast);
            setTimeout(() => {
                if (document.body.contains(toast)) {
                    document.body.removeChild(toast);
                }
            }, 3000);
        }
    }

    private handleNotificationClose(data: any): void {
        console.log('Notification closed:', data);
    }

    private handleNotificationError(data: any, error: any): void {
        console.error('Notification error:', error, data);
    }

    public isPermissionGranted(): boolean {
        return this.permission === 'granted';
    }

    public get isSupported(): boolean {
        return this._isSupported;
    }

    public getPermissionStatus(): NotificationPermission {
        return this.permission;
    }

    public clearNotificationQueue(): void {
        this.notificationQueue = [];
    }

    public getQueueLength(): number {
        return this.notificationQueue.length;
    }

    // Method to test notification sound
    public async testNotificationSound(): Promise<boolean> {
        if (!this._isSupported || this.permission !== 'granted') {
            return false;
        }

        try {
            const notification = new Notification('Test Notification', {
                body: 'This is a test notification to verify sound and vibration.',
                icon: '/favicon.png',
                silent: false,
                vibrate: [200, 100, 200]
            });

            setTimeout(() => notification.close(), 3000);
            return true;
        } catch (error) {
            console.error('Error testing notification sound:', error);
            return false;
        }
    }

    // Method to check if notifications are muted by the system
    public async checkNotificationMuted(): Promise<boolean> {
        if (!this._isSupported) {
            return true;
        }

        try {
            // Try to create a silent notification to test if notifications are working
            const testNotification = new Notification('', {
                body: '',
                silent: true
            });
            testNotification.close();
            return false; // Not muted
        } catch (error) {
            return true; // Likely muted or blocked
        }
    }
}

export default BrowserNotificationService; 